/**
 * SupportChat — the floating "Poolie" AI assistant.
 *
 * Rendered at the bottom-right of every logged-in page (via App shell), on
 * the Customer Portal (homeowners), and on Landing/Pricing (prospects).
 *
 * Props:
 *   • audience: "inspector" | "homeowner" | "prospect"
 *   • contextHints (optional): { current_page, inspection_id, user_tier }
 *     — passed into the chat request so Claude answers precisely.
 *
 * The chat endpoint is public (see backend build_support_router) so it works
 * whether or not the user is logged in.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, X, Send, Loader2, User, LifeBuoy, ArrowRight, Camera, Trash2, Copy } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_VERSION = "2026.02";

// Same session id persists per browser so Claude remembers context across page changes.
const SESSION_KEY = "poolie.session";
const HISTORY_KEY = "poolie.history";
const BREADCRUMB_KEY = "poolie.breadcrumbs";
const CONSOLE_ERR_KEY = "poolie.console_errors";

// Install a one-time global console.error hook that captures the last 10
// errors into localStorage. Escalation submissions include them automatically.
// Guarded so hot-reload doesn't stack multiple hooks.
if (typeof window !== "undefined" && !window.__poolieConsoleHook) {
  window.__poolieConsoleHook = true;
  const origErr = console.error.bind(console);
  console.error = (...args) => {
    try {
      const msg = args.map((a) => (a instanceof Error ? `${a.name}: ${a.message}` : String(a))).join(" ");
      const list = JSON.parse(localStorage.getItem(CONSOLE_ERR_KEY) || "[]");
      list.push(`${new Date().toISOString().slice(11, 19)} · ${msg.slice(0, 300)}`);
      localStorage.setItem(CONSOLE_ERR_KEY, JSON.stringify(list.slice(-10)));
    } catch { /* localStorage full — ignore */ }
    origErr(...args);
  };
  // Also capture uncaught errors
  window.addEventListener("error", (ev) => {
    try {
      const list = JSON.parse(localStorage.getItem(CONSOLE_ERR_KEY) || "[]");
      list.push(`${new Date().toISOString().slice(11, 19)} · [uncaught] ${ev.message || "error"} @ ${ev.filename || "?"}:${ev.lineno || 0}`);
      localStorage.setItem(CONSOLE_ERR_KEY, JSON.stringify(list.slice(-10)));
    } catch { /* ignore */ }
  });
}

/** Collect all the diagnostics we'll send with an escalated ticket. */
const collectDiagnostics = () => {
  const ua = navigator.userAgent;
  const width = window.innerWidth || 0;
  const isMobile = /Mobi|Android/i.test(ua);
  const isTablet = /iPad|Tablet/i.test(ua) || (width >= 600 && width < 1024 && isMobile);
  return {
    app_version: APP_VERSION,
    user_agent: ua,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    device_type: isTablet ? "tablet" : isMobile ? "mobile" : "desktop",
    breadcrumbs: safeParse(localStorage.getItem(BREADCRUMB_KEY), []),
    console_errors: safeParse(localStorage.getItem(CONSOLE_ERR_KEY), []),
  };
};

const safeParse = (raw, fallback) => {
  try { return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
};

const AUDIENCE_LABELS = {
  inspector: "Ask Poolie",
  homeowner: "Need help understanding your report?",
  prospect: "Questions? Ask Poolie",
};

const GREETINGS = {
  inspector: "G'day! I'm Poolie, your in-app assistant. Ask me anything — how to generate a report, tier questions, or troubleshooting.",
  homeowner: "Hi! I'm Poolie. I can explain any finding on your report, tell you how much a fix might cost, and help you book a re-inspection.",
  prospect: "Welcome! I'm Poolie. Ask me about pricing, features, the 14-day free trial, or anything else about Compliance Hub.",
};

const QUICK_ACTIONS = {
  inspector: [
    "How do I generate an AI report?",
    "Why is my report locked?",
    "How does the 14-day trial work?",
  ],
  homeowner: [
    "What does non-compliant mean?",
    "How do I book a re-inspection?",
    "Common fixes and prices",
  ],
  prospect: [
    "Show me the pricing",
    "Does it work offline?",
    "Can I try it before I buy?",
  ],
};

const SupportChat = ({ audience = "inspector", contextHints = {} }) => {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [escalationOpen, setEscalationOpen] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [open, messages, busy]);

  // Any other component (e.g. the Help Centre) can open the panel by
  // dispatching a `poolie:open` CustomEvent.
  useEffect(() => {
    const opener = () => setOpen(true);
    window.addEventListener("poolie:open", opener);
    return () => window.removeEventListener("poolie:open", opener);
  }, []);

  // Track a breadcrumb trail of the last N visited pages for diagnostics.
  useEffect(() => {
    try {
      const key = "poolie.breadcrumbs";
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      const here = `${window.location.pathname}${window.location.search}`;
      if (list[list.length - 1] !== here) list.push(here);
      localStorage.setItem(key, JSON.stringify(list.slice(-15)));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // Persist history so the user can close and reopen without losing thread
    try {
      const trimmed = messages.slice(-20); // keep the last 20 turns
      localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
    } catch { /* localStorage may be full — ignore */ }
  }, [messages]);

  const send = async (rawText) => {
    const text = (rawText || input).trim();
    if (!text || busy) return;
    setInput("");
    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${API_BASE}/support/chat`,
        {
          session_id: sessionId,
          audience,
          message: text,
          history: messages.slice(-10),
          current_page: contextHints.current_page || window.location.pathname,
          inspection_id: contextHints.inspection_id || null,
          user_tier: contextHints.user_tier || null,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      const assistantMsg = { role: "assistant", content: data.reply, escalate: data.should_escalate };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: "I'm having trouble connecting right now. Try again in a moment — or click 'Talk to a human' and we'll email you back.",
        escalate: true,
      }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(HISTORY_KEY);
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const showEscalateBanner = !!lastAssistant?.escalate;

  return (
    <>
      {/* Floating trigger button — single consolidated launcher */}
      <button
        data-testid="poolie-fab"
        onClick={() => { if (open) { setOpen(false); setMenuOpen(false); } else { setMenuOpen((v) => !v); } }}
        aria-label={open ? "Close Poolie" : AUDIENCE_LABELS[audience]}
        className={`fixed bottom-20 right-5 sm:bottom-6 sm:right-6 z-50 h-14 px-5 rounded-full shadow-2xl transition-all inline-flex items-center gap-2 font-bold ${open || menuOpen ? "bg-slate-900 text-white" : "bg-gradient-to-br from-[#14B8A6] to-[#0D9488] text-white hover:scale-105"}`}
      >
        {open || menuOpen ? <X size={22} strokeWidth={2.5} /> : <MessageCircle size={22} strokeWidth={2.5} />}
        <span className="hidden sm:inline text-sm">{open || menuOpen ? "Close" : AUDIENCE_LABELS[audience]}</span>
      </button>

      {/* Options menu — Ask a question / Report a problem */}
      {menuOpen && !open && (
        <div className="fixed bottom-36 right-5 sm:bottom-24 sm:right-6 z-50 w-64 bg-white border-2 border-slate-900 shadow-2xl overflow-hidden" data-testid="poolie-menu">
          <button data-testid="poolie-menu-chat"
            onClick={() => { setMenuOpen(false); setOpen(true); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-teal-50 border-b border-slate-200">
            <MessageCircle size={17} className="text-teal-700 shrink-0" strokeWidth={2.5}/>
            <span className="text-sm font-bold text-slate-900">Ask Poolie a question<span className="block text-[11px] font-normal text-slate-500">How-to help, features &amp; pricing</span></span>
          </button>
          <button data-testid="poolie-menu-guardian"
            onClick={() => { setMenuOpen(false); window.dispatchEvent(new Event("ch-open-guardian")); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-teal-50">
            <LifeBuoy size={17} className="text-teal-700 shrink-0" strokeWidth={2.5}/>
            <span className="text-sm font-bold text-slate-900">Report a problem<span className="block text-[11px] font-normal text-slate-500">Technical support, bugs &amp; suggestions</span></span>
          </button>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="poolie-panel"
          className="fixed bottom-40 sm:bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[420px] max-h-[calc(100vh-8rem)] bg-white border-2 border-slate-900 shadow-2xl flex flex-col"
          style={{ maxHeight: "min(640px, calc(100vh - 8rem))" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#1E1B4B] to-[#0F0E2E] text-white px-5 py-4 flex items-start gap-3">
            <div className="h-10 w-10 bg-teal-500/20 flex items-center justify-center flex-shrink-0">
              <LifeBuoy size={20} strokeWidth={2.5} className="text-teal-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal-300">AI Support</p>
              <h3 className="text-base font-black">Poolie</h3>
              <p className="text-[11px] text-slate-300">Answers in seconds · 24/7 · powered by Claude 4.5</p>
            </div>
            <button onClick={reset} data-testid="poolie-reset"
              className="text-slate-400 hover:text-white text-[10px] uppercase tracking-wider font-bold"
              title="Start a new conversation">
              Reset
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} data-testid="poolie-messages"
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <>
                <MessageBubble role="assistant" content={GREETINGS[audience]} />
                <div className="space-y-1.5 mt-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Try asking</p>
                  {QUICK_ACTIONS[audience].map((q) => (
                    <button key={q} onClick={() => send(q)} data-testid="poolie-quick-action"
                      className="w-full text-left px-3 py-2 bg-white border border-slate-200 hover:border-slate-900 text-sm text-slate-700 flex items-center justify-between gap-2 group">
                      <span>{q}</span>
                      <ArrowRight size={14} className="text-slate-400 group-hover:text-slate-900 shrink-0" />
                    </button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => <MessageBubble key={i} role={m.role} content={m.content} />)}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-slate-500 pl-1" data-testid="poolie-typing">
                <Loader2 size={14} className="animate-spin" /> Poolie is typing…
              </div>
            )}
          </div>

          {/* Escalation banner (shown when the LLM offers to hand off) */}
          {showEscalateBanner && (
            <div className="px-4 py-2 border-t border-amber-200 bg-amber-50 flex items-center justify-between gap-2">
              <span className="text-xs text-amber-900">Need a human?</span>
              <button onClick={() => setEscalationOpen(true)} data-testid="poolie-escalate-cta"
                className="text-xs font-bold text-amber-900 hover:underline inline-flex items-center gap-1">
                Talk to a human <ArrowRight size={12} strokeWidth={3} />
              </button>
            </div>
          )}

          {/* Input row */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t-2 border-slate-100 p-3 flex gap-2 bg-white">
            <input
              data-testid="poolie-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              disabled={busy}
              className="flex-1 h-11 px-3 text-sm border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none"
            />
            <button type="submit" disabled={!input.trim() || busy} data-testid="poolie-send"
              className="h-11 px-3 inline-flex items-center bg-[#1E1B4B] text-white disabled:opacity-40 hover:bg-[#0F0E2E]">
              <Send size={18} strokeWidth={2.5} />
            </button>
          </form>
        </div>
      )}

      <EscalateDialog
        open={escalationOpen}
        onClose={() => setEscalationOpen(false)}
        audience={audience}
        sessionId={sessionId}
        history={messages.slice(-10)}
      />
    </>
  );
};

const MessageBubble = ({ role, content }) => {
  const isAssistant = role === "assistant";
  return (
    <div data-testid={`poolie-bubble-${role}`} className={`flex gap-2 ${isAssistant ? "" : "flex-row-reverse"}`}>
      <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center ${isAssistant ? "bg-teal-100 text-teal-900" : "bg-slate-200 text-slate-700"}`}>
        {isAssistant ? <LifeBuoy size={14} strokeWidth={2.5} /> : <User size={14} strokeWidth={2.5} />}
      </div>
      <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${isAssistant ? "bg-white border border-slate-200 text-slate-800" : "bg-[#1E1B4B] text-white"}`}>
        {content}
      </div>
    </div>
  );
};

const EscalateDialog = ({ open, onClose, audience, sessionId, history }) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [screenshot, setScreenshot] = useState(null);        // { url, preview }
  const [uploadingShot, setUploadingShot] = useState(false);
  const [confirmation, setConfirmation] = useState(null);    // { ref_number, ticket_id }
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      const last = [...history].reverse().find((m) => m.role === "user");
      setSubject(last ? last.content.slice(0, 80) : "");
      setMessage(last?.content || "");
      setScreenshot(null);
      setConfirmation(null);
    }
  }, [open, history]);
  if (!open) return null;

  const attachScreenshot = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Screenshot must be under 5MB"); return; }
    setUploadingShot(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const token = localStorage.getItem("token");
      const { data } = await axios.post(`${API_BASE}/support/upload-screenshot`,
        { data_url: dataUrl },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setScreenshot({ url: data.url, preview: dataUrl });
      toast.success("Screenshot attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Screenshot upload failed");
    } finally { setUploadingShot(false); }
  };

  const submit = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Add a subject and message"); return; }
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const diag = collectDiagnostics();
      const { data } = await axios.post(`${API_BASE}/support/escalate`, {
        session_id: sessionId,
        audience,
        subject: subject.trim(),
        message: message.trim(),
        contact_email: email.trim() || undefined,
        contact_name: name.trim() || undefined,
        history,
        current_page: `${window.location.pathname}${window.location.search}`,
        screenshot_url: screenshot?.url || null,
        ...diag,
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setConfirmation({ ref_number: data.ref_number, ticket_id: data.id });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again");
    } finally { setSending(false); }
  };

  const needsEmail = !localStorage.getItem("token");

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border-2 border-slate-900 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="poolie-escalate-dialog">
        {confirmation ? (
          <TicketConfirmation confirmation={confirmation} onClose={onClose}/>
        ) : (
          <>
            <div className="bg-slate-900 text-white px-5 py-4 flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal-300">Talk to a human</p>
                <h3 className="text-base font-black mt-1">Send this to the team</h3>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
              {needsEmail && (
                <>
                  <label className="block text-xs">
                    <span className="block font-bold uppercase tracking-[0.15em] text-slate-700 mb-1">Your email</span>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="poolie-escalate-email"
                      type="email" required placeholder="you@example.com"
                      className="w-full h-10 px-3 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none" />
                  </label>
                  <label className="block text-xs">
                    <span className="block font-bold uppercase tracking-[0.15em] text-slate-700 mb-1">Your name (optional)</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} data-testid="poolie-escalate-name"
                      className="w-full h-10 px-3 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none" />
                  </label>
                </>
              )}
              <label className="block text-xs">
                <span className="block font-bold uppercase tracking-[0.15em] text-slate-700 mb-1">Subject</span>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="poolie-escalate-subject"
                  className="w-full h-10 px-3 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none" />
              </label>
              <label className="block text-xs">
                <span className="block font-bold uppercase tracking-[0.15em] text-slate-700 mb-1">Message</span>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="poolie-escalate-message"
                  rows={5} className="w-full p-3 border-2 border-slate-200 focus:border-[#1E1B4B] focus:outline-none text-sm" />
              </label>

              {/* Screenshot attachment */}
              <div>
                <span className="block font-bold uppercase tracking-[0.15em] text-slate-700 mb-1 text-xs">Screenshot (optional)</span>
                {screenshot ? (
                  <div className="relative border-2 border-slate-200 p-2" data-testid="poolie-escalate-screenshot-preview">
                    <img src={screenshot.preview} alt="Attached screenshot" className="max-h-32 mx-auto"/>
                    <button onClick={() => setScreenshot(null)} data-testid="poolie-escalate-screenshot-remove"
                      className="absolute top-1 right-1 h-7 w-7 bg-white border-2 border-slate-300 text-slate-700 hover:border-red-500 hover:text-red-600 inline-flex items-center justify-center">
                      <Trash2 size={14} strokeWidth={2.5}/>
                    </button>
                  </div>
                ) : (
                  <>
                    <input ref={fileInputRef} type="file" accept="image/png,image/jpeg" hidden
                      onChange={(e) => attachScreenshot(e.target.files?.[0])}/>
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploadingShot}
                      data-testid="poolie-escalate-screenshot-add"
                      className="h-10 px-3 inline-flex items-center gap-2 bg-white border-2 border-slate-300 text-slate-700 font-bold text-xs hover:border-slate-900 disabled:opacity-50">
                      {uploadingShot ? <Loader2 size={14} className="animate-spin"/> : <Camera size={14} strokeWidth={2.5}/>}
                      Add screenshot
                    </button>
                  </>
                )}
              </div>

              <p className="text-[10px] text-slate-500 leading-relaxed">
                We'll auto-attach diagnostics: your app version, current page, browser type, and any recent errors we captured — so we can help you faster.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <button onClick={onClose} className="h-10 px-4 bg-white border-2 border-slate-300 text-slate-700 font-bold text-sm hover:border-slate-900">Cancel</button>
              <button onClick={submit} disabled={sending} data-testid="poolie-escalate-send"
                className="h-10 px-4 inline-flex items-center gap-2 bg-[#1E1B4B] text-white font-bold text-sm hover:bg-[#0F0E2E] disabled:opacity-50">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2.5} />}
                Send
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/** After a successful escalation — shows the reference number so the user has
 *  something concrete to quote if they follow up. */
const TicketConfirmation = ({ confirmation, onClose }) => {
  const copy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(confirmation.ref_number || confirmation.ticket_id);
      toast.success("Reference copied");
    }
  };
  return (
    <div data-testid="poolie-ticket-confirmation">
      <div className="bg-emerald-600 text-white px-5 py-4">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-100">Ticket created</p>
        <h3 className="text-base font-black mt-1">We're on it — thanks!</h3>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-slate-700 leading-relaxed">
          Your ticket has been logged with the team. Walid will get back to you within 24 hours (Aus business days). We've saved your reference below — quote it if you need to follow up.
        </p>
        <div className="border-2 border-slate-900 px-4 py-3 flex items-center justify-between bg-slate-50">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Reference</span>
          <span className="font-mono font-black text-lg text-slate-900" data-testid="poolie-ticket-ref">
            {confirmation.ref_number || confirmation.ticket_id.slice(0, 8)}
          </span>
          <button onClick={copy} data-testid="poolie-ticket-copy"
            className="h-8 px-2 inline-flex items-center gap-1 text-xs font-bold text-slate-700 hover:text-slate-900">
            <Copy size={12} strokeWidth={2.5}/> Copy
          </button>
        </div>
        <ul className="text-xs text-slate-600 space-y-1">
          <li>✓ Diagnostics attached (app version, browser, current page, recent errors)</li>
          <li>✓ Screenshot attached (if you provided one)</li>
          <li>✓ Email + SMS sent to the support team</li>
          <li>✓ You'll get an email when the status changes</li>
        </ul>
      </div>
      <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex justify-end">
        <button onClick={onClose} data-testid="poolie-ticket-close"
          className="h-10 px-5 bg-[#1E1B4B] text-white font-bold text-sm hover:bg-[#0F0E2E]">
          Done
        </button>
      </div>
    </div>
  );
};

export default SupportChat;
